import { useState } from "react";
import { Bug, Lightbulb, MessageSquarePlus, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

type FeedbackType = "bug" | "enhancement" | null;

export default function FloatingFeedbackButton() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!user) return null;

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setSeverity("");
    setFeedbackType(null);
    setSubmitted(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(resetForm, 200);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return;

    setIsSubmitting(true);
    try {
      if (feedbackType === "bug") {
        await api.submitForm("bug_report", {
          email: user.email || "",
          title,
          description,
          severity: severity || "medium",
          page_url: window.location.href,
          browser: navigator.userAgent,
          os: navigator.platform,
        });
      } else {
        await api.submitForm("feature_request", {
          email: user.email || "",
          title,
          description,
          priority: severity || "medium",
          page_url: window.location.href,
        });
      }
      setSubmitted(true);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Side Tab Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed right-0 top-[58%] -translate-y-1/2 z-50 flex flex-col items-center gap-1.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-l-xl px-2 py-4 shadow-lg hover:shadow-xl transition-all duration-200"
        aria-label="Report bug or suggest enhancement"
      >
        {isOpen ? (
          <X className="w-5 h-5" />
        ) : (
          <MessageSquarePlus className="w-5 h-5" />
        )}
        <span className="text-xs font-medium [writing-mode:vertical-rl] rotate-180 tracking-wide">
          Feedback
        </span>
      </button>

      {/* Feedback Panel */}
      {isOpen && (
        <div className="fixed top-1/2 -translate-y-1/2 right-12 z-50 w-[360px] max-h-[70vh] bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col animate-in slide-in-from-right-4 fade-in duration-200">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
              {submitted
                ? "Thank you!"
                : feedbackType === "bug"
                  ? "Report a Bug"
                  : feedbackType === "enhancement"
                    ? "Suggest an Enhancement"
                    : "How can we improve?"}
            </h3>
          </div>

          <div className="overflow-y-auto p-4">
            {/* Success State */}
            {submitted && (
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Send className="w-6 h-6 text-green-600" />
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Your {feedbackType === "bug" ? "bug report" : "suggestion"} has
                  been submitted. We appreciate your feedback!
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={resetForm}
                  >
                    Submit Another
                  </Button>
                  <Button size="sm" className="flex-1" onClick={handleClose}>
                    Close
                  </Button>
                </div>
              </div>
            )}

            {/* Type Selection */}
            {!feedbackType && !submitted && (
              <div className="space-y-3">
                <button
                  onClick={() => setFeedbackType("bug")}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                    <Bug className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-900 dark:text-white">
                      Report a Bug
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Something isn't working correctly
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => setFeedbackType("enhancement")}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-900 dark:text-white">
                      Suggest an Enhancement
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Share an idea to make Ithopu better
                    </p>
                  </div>
                </button>
              </div>
            )}

            {/* Form */}
            {feedbackType && !submitted && (
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="fb-title" className="text-xs">
                    Title <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="fb-title"
                    placeholder={
                      feedbackType === "bug"
                        ? "e.g., Login button not responding"
                        : "e.g., Add dark mode to practice"
                    }
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    maxLength={200}
                    className="text-sm"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="fb-severity" className="text-xs">
                    {feedbackType === "bug" ? "Severity" : "Priority"}
                  </Label>
                  <Select value={severity} onValueChange={setSeverity}>
                    <SelectTrigger id="fb-severity" className="text-sm">
                      <SelectValue
                        placeholder={
                          feedbackType === "bug"
                            ? "How severe?"
                            : "How important?"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      {feedbackType === "bug" && (
                        <SelectItem value="critical">Critical</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="fb-description" className="text-xs">
                    Description <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="fb-description"
                    placeholder={
                      feedbackType === "bug"
                        ? "Describe what happened and what you expected..."
                        : "Describe your idea and how it would help..."
                    }
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                    rows={4}
                    maxLength={5000}
                    className="text-sm resize-none"
                  />
                </div>

                <div className="flex gap-2 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setFeedbackType(null)}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    className="flex-1"
                    disabled={isSubmitting || !title.trim() || !description.trim()}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="mr-1">Sending...</span>
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      </>
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5 mr-1" />
                        Submit
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
